// Slice 4: Advisor Thread page.
//
// One persistent thread per company. Founder posts a message. Advisor
// replies. Sia and the system can post notices. Tone follows the SIA
// contract: plain English, no slang, no metaphors.
import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, MessagesSquare, Info, User } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type AuthorKind = "founder" | "advisor" | "sia" | "system";

interface ThreadMessage {
  id: number;
  authorKind: AuthorKind;
  authorName: string;
  body: string;
  createdAt: string;
}

interface ThreadInfo {
  id: number;
  advisorName: string;
  advisorEmail: string | null;
  advisorAssigned: boolean;
}

function authHeaders(): Record<string, string> {
  return { "x-founder-token": localStorage.getItem("founder_token") || "" };
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export default function FounderAdvisorThread() {
  const [thread, setThread] = useState<ThreadInfo | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  async function load() {
    try {
      const res = await fetch(`${BASE}api/founder-portal/thread`, { headers: authHeaders() });
      if (!res.ok) {
        setError(res.status === 401 ? "Please sign in again." : "Could not load the thread.");
        return;
      }
      const data = await res.json();
      setThread(data.thread);
      setMessages(data.messages);
    } catch {
      setError("Could not load the thread.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE}api/founder-portal/thread/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j?.error || "Could not send message.");
        return;
      }
      const data = await res.json();
      setMessages((prev) => [...prev, data.message]);
      setDraft("");
    } catch {
      setError("Could not send message.");
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (error && !thread) return <div className="p-12 text-red-400 text-sm">{error}</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 flex flex-col" style={{ minHeight: "calc(100vh - 64px)" }}>
      {/* Header */}
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
          Advisor Thread
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">
          {thread?.advisorAssigned ? thread.advisorName : "Awaiting advisor assignment"}
        </h1>
        <p className="text-sm text-white/60">
          One thread between you and your Advisor. Same person every week. Every message stays here.
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 bg-black/30 border border-white/[0.08] rounded-xl p-5 space-y-4 overflow-y-auto mb-4">
        {messages.length === 0 && (
          <div className="text-center text-sm text-white/60 py-8">No messages yet.</div>
        )}
        {messages.map((m) => (
          <MessageRow key={m.id} msg={m} />
        ))}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-3 flex items-end gap-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(e as any);
          }}
          placeholder="Write a message to your Advisor"
          rows={2}
          maxLength={5000}
          data-testid="input-thread-message"
          className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none resize-none px-2 py-1.5"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="button-send-message"
          className="inline-flex items-center gap-1.5 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md px-3.5 py-2"
        >
          <Send className="w-3.5 h-3.5" />
          {sending ? "Sending" : "Send"}
        </button>
      </form>
      <p className="text-[11px] text-white/60 mt-2 px-1">
        Press Enter for a new line. Press Cmd or Ctrl + Enter to send.
      </p>
      {error && <p className="text-xs text-red-400 mt-2 px-1">{error}</p>}
    </div>
  );
}

function MessageRow({ msg }: { msg: ThreadMessage }) {
  if (msg.authorKind === "system") {
    return (
      <div className="flex items-start gap-2 text-xs text-white/60">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/60" />
        <div className="flex-1">
          <div className="leading-relaxed">{msg.body}</div>
          <div className="text-[10px] text-white/60 mt-0.5">{fmtTime(msg.createdAt)}</div>
        </div>
      </div>
    );
  }
  if (msg.authorKind === "sia") {
    return (
      <div className="bg-gradient-to-br from-[#FF5C00]/10 via-[#FF5C00]/5 to-transparent border border-[#FF5C00]/30 rounded-lg p-3.5">
        <div className="flex items-baseline gap-2 mb-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />
          <span className="text-xs font-bold">{msg.authorName}</span>
          <span className="text-[10px] text-white/60">Operating partner</span>
          <span className="text-[10px] text-white/60 ml-auto">{fmtTime(msg.createdAt)}</span>
        </div>
        <p className="text-sm text-white/85 leading-relaxed">{msg.body}</p>
      </div>
    );
  }
  if (msg.authorKind === "founder") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-[#FF5C00]/15 border border-[#FF5C00]/25 rounded-lg p-3">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-xs font-bold">{msg.authorName}</span>
            <span className="text-[10px] text-white/60 ml-auto">{fmtTime(msg.createdAt)}</span>
          </div>
          <p className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap">{msg.body}</p>
        </div>
      </div>
    );
  }
  // advisor
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] bg-white/[0.05] border border-white/[0.1] rounded-lg p-3">
        <div className="flex items-baseline gap-2 mb-1">
          <User className="w-3 h-3 text-white/60" />
          <span className="text-xs font-bold">{msg.authorName}</span>
          <span className="text-[10px] text-white/60">Your Advisor</span>
          <span className="text-[10px] text-white/60 ml-auto">{fmtTime(msg.createdAt)}</span>
        </div>
        <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{msg.body}</p>
      </div>
    </div>
  );
}
